const createJustifiedLayout = (container, items, targetHeight = 100) => {
  if (!container || !items.length || window.innerWidth < 768) return Promise.resolve();

  const loadAllImages = () => {
    const images = container.querySelectorAll('img');
    return Promise.all(Array.from(images).map(img => {
      if (img.complete) return Promise.resolve();
      return new Promise(resolve => {
        img.onload = resolve;
        img.onerror = resolve;
        setTimeout(resolve, 1000);
      });
    }));
  };
  
  return loadAllImages().then(() => {
    const containerWidth = container.offsetWidth;
    const minGap = 30;
    const maxGap = 30;
    
    Array.from(items).forEach(item => {
      item.style.position = 'static';
      item.style.width = 'auto';
      item.style.height = 'auto';
      item.style.left = 'auto';
      item.style.top = 'auto';
      item.style.marginBottom = `${minGap}px`;
    });
    
    const itemsData = Array.from(items).map(item => {
      const img = item.querySelector('img');
      if (!img) return { element: item, aspectRatio: 1, width: targetHeight };
      
      const naturalWidth = img.naturalWidth || img.offsetWidth || targetHeight;
      const naturalHeight = img.naturalHeight || img.offsetHeight || targetHeight;
      const aspectRatio = naturalWidth / naturalHeight;
      
      return {
        element: item,
        aspectRatio: aspectRatio,
        width: targetHeight * aspectRatio
      };
    });
    
    const rows = [];
    let currentRow = [];
    let currentRowWidth = 0;
    
    itemsData.forEach(itemData => {
      const itemWidth = itemData.width;
      const gapWidth = currentRow.length > 0 ? minGap : 0;
      
      if (currentRow.length === 0 || (currentRowWidth + gapWidth + itemWidth) <= containerWidth) {
        currentRow.push(itemData);
        currentRowWidth += itemWidth + gapWidth;
      } else {
        if (currentRow.length > 0) {
          rows.push([...currentRow]);
        }
        currentRow = [itemData];
        currentRowWidth = itemWidth;
      }
    });
    
    if (currentRow.length > 0) {
      rows.push(currentRow);
    }
    
    container.style.position = 'relative';
    let currentTop = 0;
    
    rows.forEach((row, rowIndex) => {
      const isLastRow = rowIndex === rows.length - 1;
      const totalItemWidth = row.reduce((sum, item) => sum + item.width, 0);
      const availableSpace = containerWidth - totalItemWidth;
      const gapCount = Math.max(1, row.length - 1);
      
      let gapSize = minGap;
      
      if (row.length === 1) {
        gapSize = 0;
        row.forEach(itemData => {
          itemData.scaledWidth = itemData.width;
        });
      } else if (isLastRow) {
        gapSize = minGap;
        row.forEach(itemData => {
          itemData.scaledWidth = itemData.width;
        });
      } else {
        const totalGapsNeeded = row.length - 1;
        gapSize = availableSpace / totalGapsNeeded;
        
        if (gapSize > maxGap) {
          const totalGapSpace = totalGapsNeeded * maxGap;
          const availableForItems = containerWidth - totalGapSpace;
          const scaleFactor = availableForItems / totalItemWidth;
          
          row.forEach(itemData => {
            itemData.scaledWidth = itemData.width * scaleFactor;
          });
          gapSize = maxGap;
        } else {
          row.forEach(itemData => {
            itemData.scaledWidth = itemData.width;
          });
        }
      }
      
      let currentLeft = 0;
      let maxRowHeight = 0;
      const isLastRowAndSparse = isLastRow && row.length < Math.floor(containerWidth / (targetHeight * 1.5));
      
      row.forEach((itemData, index) => {
        const item = itemData.element;
        const img = item.querySelector('img');
        const itemWidth = itemData.scaledWidth || itemData.width;
        
        if (img) {
          img.style.height = `${targetHeight}px`;
          img.style.width = `${itemWidth}px`;
          img.style.objectFit = 'cover';
        }
        
        item.style.position = 'absolute';
        item.style.left = `${currentLeft}px`;
        item.style.top = `${currentTop}px`;
        item.style.width = `${itemWidth}px`;
        item.style.height = 'auto';
        
        item.style.marginRight = '0';
        
        if (isLastRowAndSparse && index === row.length - 1) {
          item.style.marginRight = 'auto';
        }
        
        const gapToUse = isLastRowAndSparse ? minGap : gapSize;
        currentLeft += itemWidth + (index < row.length - 1 ? gapToUse : 0);
        
        setTimeout(() => {
          maxRowHeight = Math.max(maxRowHeight, item.offsetHeight);
        }, 0);
      });
      
      currentTop += targetHeight + minGap;
    });
    
    const updateContainerHeight = () => {
      if (window.innerWidth < 768) return;

      let maxBottom = 0;
      Array.from(items).forEach(item => {
        const itemBottom = item.offsetTop + item.offsetHeight;
        maxBottom = Math.max(maxBottom, itemBottom);
      });

      if (maxBottom > 0) {
        container.style.height = `${maxBottom + minGap}px`;
      }
    };

    setTimeout(() => updateContainerHeight(), 50);
    setTimeout(() => updateContainerHeight(), 150);
    setTimeout(() => updateContainerHeight(), 300);
    
    return { updateHeight: updateContainerHeight };
  });
};

const scaleControl = () => {
  const scaleRange = document.querySelector('#scale-range');
  const minusButton = document.querySelector('#scale-minus');
  const plusButton = document.querySelector('#scale-plus');
  const items = document.querySelectorAll('.projects__list .projects__item');
  const container = document.querySelector('.projects__list');
  
  if (!(scaleRange && minusButton && plusButton && items.length && container)) return;
  
  const scaleSteps = ['small', 'medium', 'regular', 'large'];
  const targetHeights = {
    small: 60,
    medium: 120,
    regular: 160,
    large: 200
  };
  
  const defaultStepIndex = 0;
  
  const getCachedStep = () => {
    const stored = localStorage.getItem('scaleStep');
    if (stored !== null && stored !== undefined && !isNaN(stored)) {
      const parsedStep = parseInt(stored);
      if (parsedStep >= 0 && parsedStep < scaleSteps.length) {
        return parsedStep;
      }
    }
    return defaultStepIndex;
  };
  
  let currentStepIndex = getCachedStep();
  let resizeTimeout;
  let isUpdating = false;
  
  const updateProgressBar = () => {
    try {
      const progress = (currentStepIndex / (scaleSteps.length - 1)) * 100;

      if (scaleRange) {
        scaleRange.style.setProperty('--progress', `${progress}%`);

        // Also update the wrapper for the visual progress overlay
        const wrapper = scaleRange.closest('.scale-range-wrapper');
        if (wrapper) {
          wrapper.style.setProperty('--progress', `${progress}%`);
        }

        scaleRange.offsetHeight;
        scaleRange.value = currentStepIndex;
        const event = new Event('input', { bubbles: true });
        scaleRange.dispatchEvent(event);
      }
    } catch (error) {
      console.error('Error updating progress bar:', error);
    }
  };
  
  const updateControlStates = () => {
    try {
      if (minusButton) {
        minusButton.disabled = currentStepIndex <= 0;
        minusButton.setAttribute('aria-disabled', currentStepIndex <= 0);
      }
      
      if (plusButton) {
        plusButton.disabled = currentStepIndex >= scaleSteps.length - 1;
        plusButton.setAttribute('aria-disabled', currentStepIndex >= scaleSteps.length - 1);
      }
      
      localStorage.setItem('scaleStep', currentStepIndex.toString());
      updateProgressBar();
      
    } catch (error) {
      console.error('Error updating control states:', error);
    }
  };
  
  const updateLayout = () => {
    if (isUpdating) return;
    
    isUpdating = true;
    const step = scaleSteps[currentStepIndex];
    const targetHeight = targetHeights[step];
    
    container.classList.add('is-hidden');
    updateControlStates();
    
    setTimeout(() => {
      createJustifiedLayout(container, Array.from(items), targetHeight)
        .then((result) => {
          setTimeout(() => {
            try {
              if (result && result.updateHeight) {
                result.updateHeight();
              } else {
                if (window.innerWidth >= 768) {
                  let maxBottom = 0;
                  Array.from(items).forEach(item => {
                    const itemBottom = item.offsetTop + item.offsetHeight;
                    maxBottom = Math.max(maxBottom, itemBottom);
                  });
                  if (maxBottom > 0) {
                    container.style.height = `${maxBottom + 10}px`;
                  }
                }
              }
              
              setTimeout(() => {
                updateProgressBar();
              }, 10);
              
            } catch (error) {
              console.error('Error in layout completion:', error);
            } finally {
              container.classList.remove('is-hidden');
              isUpdating = false;
            }
          }, 50);
        })
        .catch((error) => {
          console.error('Error in createJustifiedLayout:', error);
          container.classList.remove('is-hidden');
          isUpdating = false;
          updateProgressBar();
        });
    }, 50);
  };
  
  const handleInput = (e) => {
    if (isUpdating) return;
    
    const newIndex = parseInt(e.target.value);
    if (newIndex !== currentStepIndex && newIndex >= 0 && newIndex < scaleSteps.length) {
      currentStepIndex = newIndex;
      updateLayout();
    }
  };
  
  const handleMinus = (e) => {
    e.preventDefault();
    if (currentStepIndex > 0 && !isUpdating) {
      currentStepIndex--;
      updateLayout();
    }
  };
  
  const handlePlus = (e) => {
    e.preventDefault();
    if (currentStepIndex < scaleSteps.length - 1 && !isUpdating) {
      currentStepIndex++;
      updateLayout();
    }
  };
  
  const handleResize = () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      if (!isUpdating) {
        updateLayout();
      }
    }, 300);
  };
  
  try {
    scaleRange.min = 0;
    scaleRange.max = scaleSteps.length - 1;
    scaleRange.step = 1;
    scaleRange.value = currentStepIndex;
    
    updateProgressBar();
    
    scaleRange.removeEventListener('input', handleInput);
    minusButton.removeEventListener('click', handleMinus);
    plusButton.removeEventListener('click', handlePlus);
    window.removeEventListener('resize', handleResize);
    
    scaleRange.addEventListener('input', handleInput);
    minusButton.addEventListener('click', handleMinus);
    plusButton.addEventListener('click', handlePlus);
    window.addEventListener('resize', handleResize);
    
    setTimeout(() => {
      updateLayout();
    }, 100);
    
  } catch (error) {
    console.error('Error initializing scale control:', error);
  }
};

export { scaleControl, createJustifiedLayout };